import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import { DEFAULT_CSV_COLUMNS } from '../constants/payoutManagerConstants';
import ColumnPicker from './ColumnPicker';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const CombinedPayoutDownloadModal = ({ open, onClose, combinedCsvPath, csvColumns, recentPayouts }) => {
  const [selectedPayoutIds, setSelectedPayoutIds] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_CSV_COLUMNS);
  const [groupByEvent, setGroupByEvent] = useState(false);
  const [includeCash, setIncludeCash] = useState(false);
  const [includeTotals, setIncludeTotals] = useState(false);
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const togglePayout = (id) => {
    setSelectedPayoutIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const toggleColumn = (key) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((col) => col !== key) : [...prev, key]
    );
  };

  const handleDownload = () => {
    if (selectedPayoutIds.length === 0 || selectedColumns.length === 0) return;

    const params = new URLSearchParams();
    selectedPayoutIds.forEach((id) => params.append("payout_ids[]", id));
    selectedColumns.forEach((col) => params.append("columns[]", col));
    params.append("group_by_event", groupByEvent.toString());
    params.append("include_cash", includeCash.toString());
    params.append("include_totals", includeTotals.toString());

    const separator = combinedCsvPath.includes("?") ? "&" : "?";
    window.location.href = combinedCsvPath + separator + params.toString();
    onClose();
  };

  const canSubmit = selectedPayoutIds.length > 0 && selectedColumns.length > 0;

  return (
    <Popup
      modal
      open={open}
      onClose={onClose}
      closeOnDocumentClick
      className="popup-modal"
      contentStyle={{
        textAlign: "left",
        border: "none",
        borderRadius: "8px",
        boxShadow: "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
        width: "600px",
        maxHeight: "85vh",
        padding: "24px",
        overflowY: "auto"
      }}
    >
      {(close) => (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Combined Payout Export</h5>
            <a href="#" onClick={(e) => { e.preventDefault(); close(); }}
              className="h4 mb-0 text-muted text-decoration-none">
              &times;
            </a>
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="mb-0"><strong>Select Payouts</strong></label>
              <div>
                <a href="#" onClick={(e) => { e.preventDefault(); setSelectedPayoutIds(recentPayouts.map((p) => p.id)); }}
                  className="small mr-3">
                  Select all
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); setSelectedPayoutIds([]); }}
                  className="small">
                  Deselect all
                </a>
              </div>
            </div>
            <div className="border rounded" style={{ maxHeight: "250px", overflowY: "auto" }}>
              {recentPayouts.map((payout) => (
                <label
                  key={payout.id}
                  className={`d-flex align-items-center px-3 py-2 mb-0 border-bottom cursor-pointer ${selectedPayoutIds.includes(payout.id) ? "bg-light" : ""}`}
                >
                  <input
                    type="checkbox"
                    className="mr-3"
                    checked={selectedPayoutIds.includes(payout.id)}
                    onChange={() => togglePayout(payout.id)}
                  />
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex justify-content-between">
                      <strong className="small">{payout.venue}</strong>
                      <span className="small">{formatter.format(payout.amount)}</span>
                    </div>
                    <div className="small text-muted">
                      {payout.date}
                      {payout.events ? ` \u2014 ${payout.events}` : ""}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-1 small text-muted">
              {selectedPayoutIds.length} payout{selectedPayoutIds.length !== 1 ? "s" : ""} selected
            </div>
          </div>

          <div className="mb-3">
            <label className="d-block mb-2"><strong>Options</strong></label>
            <div className="form-check mb-1">
              <input
                type="checkbox"
                className="form-check-input"
                id="combinedGroupByEvent"
                checked={groupByEvent}
                onChange={() => setGroupByEvent(!groupByEvent)}
              />
              <label className="form-check-label" htmlFor="combinedGroupByEvent">
                Group by event
              </label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="combinedIncludeCash"
                checked={includeCash}
                onChange={() => setIncludeCash(!includeCash)}
              />
              <label className="form-check-label" htmlFor="combinedIncludeCash">
                Include cash transactions
              </label>
            </div>
            <div className="form-check mt-1">
              <input
                type="checkbox"
                className="form-check-input"
                id="combinedIncludeTotals"
                checked={includeTotals}
                onChange={() => setIncludeTotals(!includeTotals)}
              />
              <label className="form-check-label" htmlFor="combinedIncludeTotals">
                Include totals row
              </label>
            </div>
          </div>

          <ColumnPicker
            csvColumns={csvColumns}
            selectedColumns={selectedColumns}
            onToggle={toggleColumn}
            onSelectAll={() => setSelectedColumns(csvColumns.map((col) => col.key))}
            onDeselectAll={() => setSelectedColumns([])}
            collapsible
            showColumnPicker={showColumnPicker}
            onToggleVisibility={() => setShowColumnPicker(!showColumnPicker)}
          />

          <div className="d-flex justify-content-end">
            <button className="btn btn-setlist-secondary mr-2" onClick={close}>
              Cancel
            </button>
            <button
              className="btn btn-setlist-primary"
              onClick={handleDownload}
              disabled={!canSubmit}
            >
              Download Combined CSV
            </button>
          </div>
        </div>
      )}
    </Popup>
  );
};

CombinedPayoutDownloadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  combinedCsvPath: PropTypes.string.isRequired,
  csvColumns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  recentPayouts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      venue: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      events: PropTypes.string,
      amount: PropTypes.number.isRequired
    })
  ).isRequired
};

export default CombinedPayoutDownloadModal;
